// src/components/LanguagePickerModal.js
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../context/ThemeContext';

// Supported languages with native names
const SUPPORTED_LANGUAGES = [
    { code: 'en', nativeName: 'English' },
    { code: 'ar', nativeName: 'العربية' },
    { code: 'de', nativeName: 'Deutsch' },
    { code: 'es', nativeName: 'Español' },
    { code: 'fr', nativeName: 'Français' },
    { code: 'it', nativeName: 'Italiano' },
    { code: 'ja', nativeName: '日本語' },
    { code: 'ko', nativeName: '한국어' },
    { code: 'pt', nativeName: 'Português' },
    { code: 'ru', nativeName: 'Русский' },
    { code: 'tr', nativeName: 'Türkçe' },
    { code: 'zh', nativeName: '中文' },
];

// Spinner component
const Spinner = ({ size = 16, color = 'currentColor' }) => (
    <svg
        className="animate-spin"
        style={{ width: size, height: size }}
        viewBox="0 0 24 24"
        fill="none"
    >
        <circle
            className="opacity-25"
            cx="12"
            cy="12"
            r="10"
            stroke={color}
            strokeWidth="3"
            fill="none"
        />
        <path
            className="opacity-75"
            fill={color}
            d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
        />
    </svg>
);

// Checkbox component
const Checkbox = ({ checked, onChange, label, isForest, theme }) => (
    <div
        onClick={onChange}
        className="flex items-center gap-3 px-3 py-2.5 rounded-lg cursor-pointer transition-colors"
        style={{
            backgroundColor: isForest ? 'rgba(15,58,48,0.6)' : 'rgba(241,245,249,0.8)',
        }}
    >
        <div
            className="w-5 h-5 rounded flex items-center justify-center border-2 transition-colors"
            style={{
                borderColor: checked ? theme.interactive.primary : (isForest ? '#81c784' : '#9ca3af'),
                backgroundColor: checked ? theme.interactive.primary : 'transparent',
            }}
        >
            {checked && (
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3">
                    <polyline points="20 6 9 17 4 12" />
                </svg>
            )}
        </div>
        <span style={{ color: isForest ? '#e8f5e9' : theme.text.primary }} className="text-sm font-medium">
            {label}
        </span>
    </div>
);

export default function LanguagePickerModal({
    open,
    onClose,
    onSelect,
    title,
    showPpmOption = false,
    showMarketPricesOption = false,
    metalsLoading = false,
}) {
    const { t, i18n } = useTranslation();
    const { theme, isForest } = useTheme();

    const currentLang = i18n.language?.split('-')[0] || 'en';
    const [selectedLang, setSelectedLang] = React.useState(currentLang);
    const [includePpm, setIncludePpm] = React.useState(false);
    const [includeMarketPrices, setIncludeMarketPrices] = React.useState(false);
    const [isGenerating, setIsGenerating] = React.useState(false);

    // Reset state when modal opens
    React.useEffect(() => {
        if (open) {
            setSelectedLang(currentLang);
            setIncludePpm(false);
            setIncludeMarketPrices(false);
            setIsGenerating(false);
        }
    }, [open, currentLang]);

    // Sort languages to put current language at the top
    const sortedLanguages = React.useMemo(() => {
        const current = SUPPORTED_LANGUAGES.find(l => l.code === currentLang);
        const others = SUPPORTED_LANGUAGES.filter(l => l.code !== currentLang);
        return current ? [current, ...others] : SUPPORTED_LANGUAGES;
    }, [currentLang]);

    const handleConfirm = async () => {
        if (isGenerating) return;
        setIsGenerating(true);
        try {
            await onSelect(selectedLang, includePpm, includeMarketPrices);
            onClose();
        } catch (e) {
            console.error('PDF generation failed:', e);
        } finally {
            setIsGenerating(false);
        }
    };

    if (!open) return null;

    const cardStyle = {
        backgroundColor: isForest ? theme.surface.card : theme.surface.card,
        border: `1px solid ${isForest ? 'rgba(255,255,255,0.12)' : theme.surface.cardBorder}`,
        boxShadow: isForest ? '0 4px 20px rgba(0,0,0,0.4)' : '0 4px 20px rgba(0,0,0,0.15)',
    };

    return (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
            <div className="rounded-xl max-w-sm w-full max-h-[85vh] overflow-hidden" style={cardStyle}>
                {/* Header */}
                <div
                    className="p-4 flex items-center justify-between"
                    style={{ borderBottom: `1px solid ${isForest ? 'rgba(255,255,255,0.1)' : theme.surface.cardBorder}` }}
                >
                    <h2 className="text-lg font-semibold" style={{ color: isForest ? '#e8f5e9' : theme.text.primary }}>
                        {title || t('pdf.selectLanguage')}
                    </h2>
                    <button
                        onClick={onClose}
                        className="p-1 rounded-md hover:bg-white/10 transition-colors"
                        style={{ color: isForest ? '#a5d6a7' : theme.text.tertiary }}
                    >
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M18 6L6 18M6 6l12 12" />
                        </svg>
                    </button>
                </div>

                {/* Language List */}
                <div className="p-3 max-h-64 overflow-y-auto space-y-2">
                    {sortedLanguages.map((lang, index) => {
                        const isSelected = selectedLang === lang.code;
                        const isCurrentLang = lang.code === currentLang;
                        const translatedName = t(`languages.${lang.code}`);

                        return (
                            <button
                                key={lang.code}
                                onClick={() => setSelectedLang(lang.code)}
                                className={`w-full flex items-center justify-between p-3 rounded-lg transition-all ${index === 0 && isCurrentLang ? 'mb-3' : ''
                                    }`}
                                style={{
                                    backgroundColor: isSelected
                                        ? (isForest ? 'rgba(16, 185, 129, 0.2)' : 'rgba(16, 185, 129, 0.1)')
                                        : (isForest ? 'rgba(15,58,48,0.6)' : 'rgba(241,245,249,0.8)'),
                                    border: `1px solid ${isSelected ? theme.interactive.primary : (isForest ? 'rgba(255,255,255,0.05)' : 'rgba(203,213,225,0.5)')}`,
                                }}
                            >
                                <div className="text-left">
                                    <div
                                        className="font-medium"
                                        style={{ color: isForest ? '#e8f5e9' : theme.text.primary }}
                                    >
                                        {lang.nativeName}
                                    </div>
                                    <div
                                        className="text-sm"
                                        style={{ color: isForest ? '#a5d6a7' : theme.text.secondary }}
                                    >
                                        {translatedName}
                                    </div>
                                </div>
                                {isSelected && (
                                    <svg
                                        width="20"
                                        height="20"
                                        viewBox="0 0 24 24"
                                        fill="none"
                                        stroke={theme.interactive.primary}
                                        strokeWidth="2.5"
                                    >
                                        <polyline points="20 6 9 17 4 12" />
                                    </svg>
                                )}
                            </button>
                        );
                    })}
                </div>

                {/* Options (PPM and Market Prices) */}
                {(showPpmOption || showMarketPricesOption) && (
                    <div
                        className="p-3 space-y-2"
                        style={{ borderTop: `1px solid ${isForest ? 'rgba(255,255,255,0.1)' : theme.surface.cardBorder}` }}
                    >
                        {showPpmOption && (
                            <Checkbox
                                checked={includePpm}
                                onChange={() => setIncludePpm(!includePpm)}
                                label={t('pdf.includePpmData')}
                                isForest={isForest}
                                theme={theme}
                            />
                        )}
                        {showMarketPricesOption && (
                            <Checkbox
                                checked={includeMarketPrices}
                                onChange={() => setIncludeMarketPrices(!includeMarketPrices)}
                                label={t('pdf.includeMarketPrices')}
                                isForest={isForest}
                                theme={theme}
                            />
                        )}
                    </div>
                )}

                {/* Footer */}
                <div
                    className="p-3 flex justify-end gap-2"
                    style={{ borderTop: `1px solid ${isForest ? 'rgba(255,255,255,0.1)' : theme.surface.cardBorder}` }}
                >
                    <button
                        onClick={onClose}
                        disabled={isGenerating}
                        className="px-4 py-2 text-sm font-medium rounded-lg transition-colors disabled:opacity-50"
                        style={{ color: isForest ? '#a5d6a7' : theme.text.secondary }}
                    >
                        {t('common.cancel')}
                    </button>
                    <button
                        onClick={handleConfirm}
                        disabled={isGenerating || (includePpm && metalsLoading)}
                        className="px-4 py-2 text-sm font-medium text-white rounded-lg flex items-center gap-2 transition-colors disabled:opacity-70"
                        style={{ backgroundColor: (isGenerating || (includePpm && metalsLoading)) ? '#6b7280' : theme.interactive.primary }}
                    >
                        {(isGenerating || (includePpm && metalsLoading)) && <Spinner size={16} color="white" />}
                        {isGenerating ? t('pdf.generating') : (includePpm && metalsLoading) ? t('common.loading') : t('common.confirm')}
                    </button>
                </div>
            </div>
        </div>
    );
}
